import {
  Check, Minus
} from 'lucide-react'

function stepCircleStyle(step: number, currentStep: number, skipped: boolean): string {
  // A skipped step is behind the user but not done — it gets a muted circle and
  // a dash, never the completed check.
  if (skipped) return 'bg-gray-300 text-white'
  if (step < currentStep) return 'bg-primary-500 text-white'
  if (step === currentStep) return 'bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-soft'
  return 'bg-gray-200 text-gray-400'
}

interface StepIndicatorProps {
  currentStep: number
  labels?: string[]
  // Steps the user moved past without completing (today: the Google
  // permissions step, when they chose to set it up later).
  skippedSteps?: number[]
}

export function StepIndicator({
  currentStep,
  labels = ['Choose', 'Connect', 'Ready!'],
  skippedSteps = [],
}: StepIndicatorProps): JSX.Element {
  const steps = labels.map((_, i) => i + 1)
  const skipped = new Set(skippedSteps)

  return (
    <div className="flex items-center justify-center w-full pb-6">
      {steps.map((step, index) => (
        <div
          key={step}
          className={`flex items-center ${index < steps.length - 1 ? 'flex-1 min-w-0' : ''}`}
        >
          <div className="relative shrink-0">
            <div
              className={`flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full transition-colors duration-300 text-sm font-semibold ${stepCircleStyle(step, currentStep, skipped.has(step))}`}
            >
              {skipped.has(step) ? (
                <Minus className="w-4 h-4" />
              ) : step < currentStep ? (
                <Check className="w-4 h-4" />
              ) : (
                <span>{step}</span>
              )}
            </div>
            <span
              className={`absolute top-full left-1/2 -translate-x-1/2 mt-1.5 text-xs font-medium transition-colors duration-300 whitespace-nowrap ${
                step === currentStep ? 'inline' : 'hidden sm:inline'
              } ${step <= currentStep && !skipped.has(step) ? 'text-primary-600' : 'text-gray-400'}`}
            >
              {labels[index]}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`h-0.5 flex-1 mx-1 sm:mx-2 transition-colors duration-500 rounded-full ${
                step < currentStep ? 'bg-primary-500' : 'bg-gray-200'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  )
}
